import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { HiExternalLink, HiCode, HiSparkles, HiPlay, HiPause, HiX, HiGlobe, HiChat, HiDownload, HiCog } from 'react-icons/hi';
import { FaGithub, FaWhatsapp } from 'react-icons/fa';
import ScrollReveal from './ScrollReveal';

const Projects = ({ projects = [] }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-100px" });
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState('all');

  // Default projects if none provided from API
  const defaultProjects = [
    {
      id: 'gta-website',
      title: 'GTA 1.27 Website',
      description: 'Official website for GTA 1.27 with modern design and seamless user experience.',
      long_description: 'A comprehensive website for GTA 1.27 featuring modern design principles, responsive layout, and optimized performance. The site serves as the official platform for the GTA 1.27 community with integrated features and user management.',
      category: 'Web Development',
      status: 'active',
      demo_url: 'https://gtao.me/',
      github_url: null,
      image_url: null,
      technologies: ['HTML', 'CSS', 'JavaScript', 'React', 'Tailwind CSS']
    },
    {
      id: 'snapmatic',
      title: 'Snapmatic Revived',
      description: 'Photo sharing platform integrated with GTA 1.27, allowing players to share in-game moments.',
      long_description: 'Snapmatic Revived is a photo sharing platform specifically designed for the GTA 1.27 community. Players can upload, share, and discover in-game screenshots and moments. Features include user authentication, photo galleries, and social sharing capabilities.',
      category: 'Web Development',
      status: 'active',
      demo_url: 'https://gtao.me/snapmatic',
      github_url: null,
      image_url: null,
      technologies: ['React', 'Node.js', 'MongoDB', 'AWS S3', 'Express.js']
    },
    {
      id: 'ninja-revived',
      title: 'NiNJA REVIVED Website',
      description: 'Official website for NiNJA REVIVED Xbox 360 JTAG/RGH service with modern design and user experience.',
      long_description: 'A comprehensive website for NiNJA REVIVED featuring modern design principles, responsive layout, and optimized performance. The site serves as the official platform for the NiNJA REVIVED Xbox 360 JTAG/RGH service with integrated features and user management.',
      category: 'Web Development',
      status: 'active',
      demo_url: 'https://xbninja.com/',
      github_url: null,
      image_url: null,
      technologies: ['HTML', 'CSS', 'JavaScript', 'React', 'Tailwind CSS']
    },
    {
      id: 'smvad',
      title: 'SMVAD',
      description: 'Social Media Video & Audio Downloader supporting TikTok, Instagram, YouTube, and Spotify.',
      long_description: 'SMVAD is a comprehensive social media downloader that supports multiple platforms including TikTok, Instagram, YouTube, and Spotify. Users can download videos without watermarks, Instagram posts and stories, YouTube videos and audio, and Spotify tracks as MP3 files.',
      category: 'Web Development',
      status: 'active',
      demo_url: 'https://smvadb-1.onrender.com/',
      github_url: null,
      image_url: null,
      technologies: ['Python', 'Flask', 'yt-dlp', 'FFmpeg', 'Heroku']
    },
    {
      id: 'aliyah-chatbot',
      title: 'Aliyah - WhatsApp AI Bot',
      description: 'Advanced AI chatbot indistinguishable from human conversation, available on WhatsApp.',
      long_description: 'Aliyah is an advanced AI chatbot designed to be indistinguishable from human conversation. Built with cutting-edge natural language processing, it provides intelligent responses and maintains context throughout conversations. Available 24/7 on WhatsApp for seamless communication.',
      category: 'AI/ML',
      status: 'active',
      demo_url: null,
      github_url: null,
      image_url: null,
      technologies: ['Python', 'OpenAI API', 'WhatsApp Business API', 'NLP', 'Machine Learning'],
      contact: '+27 67 324 7197'
    },
    {
      id: 'github-stalker',
      title: 'GitHub Stalker Discord Bot',
      description: 'Discord bot that monitors GitHub users and repositories, sending private notifications for new activity.',
      long_description: 'A Discord bot that allows users to "stalk" GitHub users and repositories. The bot monitors specified accounts and sends private Discord DMs when new activity is detected, including new commits, repositories, and profile updates. Features include user and repository stalking with customizable notifications.',
      category: 'Discord Bot',
      status: 'active',
      demo_url: null,
      github_url: 'https://github.com/kryptik-dev/github_stalker',
      image_url: null,
      technologies: ['JavaScript', 'Discord.js', 'GitHub API', 'Node.js', 'JSON Database']
    },
    {
      id: 'xbox-activity-portal',
      title: 'Xbox Activity Portal',
      description: 'Desktop application for Xbox 360 activity monitoring with Discord RPC integration.',
      long_description: 'A desktop application that displays and shares Xbox 360 activity, including game presence and Discord integration. Built with Electron and React, it provides a modern interface for monitoring Xbox activity and integrates seamlessly with Discord Rich Presence for enhanced social gaming experience.',
      category: 'Desktop App',
      status: 'active',
      demo_url: null,
      github_url: 'https://github.com/kryptik-dev/xbox-activity-portal',
      image_url: null,
      technologies: ['Electron', 'React', 'Node.js', 'Discord RPC', 'TypeScript']
    }
  ];

  // Use default projects if no projects provided from API
  const allProjects = projects.length > 0 ? projects : defaultProjects;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  const categories = ['all', 'Web Development', 'AI/ML', 'Gaming', 'Discord Bot', 'Desktop App'];
  const filteredProjects = filter === 'all' 
    ? allProjects 
    : allProjects.filter(project => project.category === filter);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'accent-green';
      case 'discontinued': return 'red-500';
      case 'coming_soon': return 'accent-blue';
      default: return 'accent-green';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'discontinued': return 'Discontinued';
      case 'coming_soon': return 'Coming Soon';
      default: return 'Active';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Web Development': return <HiGlobe />;
      case 'AI/ML': return <HiSparkles />;
      case 'Gaming': return <HiPlay />;
      case 'Discord Bot': return <HiChat />;
      case 'Desktop App': return <HiCog />;
      default: return <HiCode />;
    }
  };

  const ProjectModal = ({ project, onClose }) => (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="relative glass-morphism-strong rounded-2xl p-4 md:p-8 max-w-4xl w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 md:top-6 right-4 md:right-6 p-2 glass-morphism rounded-lg hover:bg-glass-medium transition-colors"
          >
            <HiX size={20} className="md:w-6 md:h-6" />
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            <div className="space-y-6">
              {/* Project Header */}
              <div className="flex items-center space-x-3 md:space-x-4 mb-4 md:mb-6">
                <div className="text-3xl md:text-4xl text-accent-green opacity-80">
                  {getCategoryIcon(project.category)}
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-space-grotesk font-bold text-white">
                    {project.title}
                  </h2>
                  <span className="text-xs md:text-sm font-jetbrains text-accent-green bg-accent-green/10 px-2 md:px-3 py-1 rounded-full">
                    {project.category}
                  </span>
                </div>
              </div>

              {/* Project Description */}
              <div>
                <h4 className="text-base md:text-lg font-semibold text-white mb-2 md:mb-3">Description</h4>
                <p className="text-gray-300 leading-relaxed text-sm md:text-base">
                  {project.long_description || project.description}
                </p>
              </div>

              {/* Technologies */}
              <div>
                <h4 className="text-base md:text-lg font-semibold text-white mb-2 md:mb-3">Technologies Used</h4>
                <div className="flex flex-wrap gap-2">
                  {project.technologies?.map((tech, index) => (
                    <span
                      key={index}
                      className="px-2 md:px-3 py-1 bg-glass-light rounded-full text-xs md:text-sm font-jetbrains text-accent-green border border-accent-green/30"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-start">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-${getStatusColor(project.status)}/20 text-${getStatusColor(project.status)} border border-${getStatusColor(project.status)}/30`}>
                  {getStatusText(project.status)}
                </span>
              </div>

              {/* Contact Info for Aliyah */}
              {project.id === 'aliyah-chatbot' && (
                <div className="p-3 md:p-4 bg-accent-green/10 border border-accent-green/30 rounded-lg">
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <FaWhatsapp className="text-accent-green text-lg md:text-xl" />
                    <div>
                      <p className="text-accent-green font-semibold text-sm md:text-base">Message Aliyah on WhatsApp</p>
                      <p className="text-white text-sm md:text-base">{project.contact}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Status Messages */}
              {project.status === 'discontinued' && (
                <div className="p-3 md:p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-xs md:text-sm">
                    <strong>PROJECT DISCONTINUED:</strong> This project has been discontinued 
                    but the concepts and learnings continue to influence other work.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                {project.demo_url && (
                  <motion.a
                    href={project.demo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-primary flex items-center justify-center space-x-2 text-sm md:text-base px-4 md:px-6 py-2 md:py-3"
                  >
                    <HiExternalLink />
                    <span>Live Demo</span>
                  </motion.a>
                )}
                
                {project.github_url && (
                  <motion.a
                    href={project.github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-secondary flex items-center justify-center space-x-2 text-sm md:text-base px-4 md:px-6 py-2 md:py-3"
                  >
                    <FaGithub />
                    <span>View Code</span>
                  </motion.a>
                )}

                {project.id === 'aliyah-chatbot' && (
                  <motion.a
                    href={`https://wa.me/${project.contact.replace(/\s/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-primary flex items-center justify-center space-x-2 text-sm md:text-base px-4 md:px-6 py-2 md:py-3"
                  >
                    <FaWhatsapp />
                    <span>Chat with Aliyah</span>
                  </motion.a>
                )}
              </div>

              {/* Additional Info */}
              <div className="pt-4 md:pt-6 border-t border-glass-medium">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 text-xs md:text-sm">
                  <div>
                    <span className="text-gray-400">Category:</span>
                    <span className="text-white ml-2">{project.category}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Status:</span>
                    <span className={`text-${getStatusColor(project.status)} ml-2`}>
                      {getStatusText(project.status)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return (
    <section id="projects" ref={ref} className="py-32 relative overflow-hidden scroll-mt-20">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-40 right-20 w-80 h-80 opacity-10">
          <div className="w-full h-full bg-accent-blue rounded-full animate-pulse-glow" style={{ filter: 'blur(80px)' }} />
        </div>
        <div className="absolute bottom-40 left-20 w-96 h-96 opacity-10">
          <div className="w-full h-full bg-accent-green rounded-full animate-pulse-glow" style={{ filter: 'blur(100px)' }} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-8 md:mb-12 lg:mb-16">
            <div className="inline-flex items-center space-x-2 glass-morphism px-4 md:px-6 py-2 md:py-3 rounded-full mb-4 md:mb-6">
              <HiSparkles className="text-accent-green" />
              <span className="font-jetbrains text-accent-green tracking-wider text-xs md:text-sm">FEATURED WORK</span>
            </div>
            <h2 className="text-display-2 font-space-grotesk font-bold mb-4 md:mb-6">
              <span className="text-white">Selected </span>
              <span className="gradient-text-green">Projects</span>
            </h2>
            <div className="max-w-5xl mx-auto px-4">
              <ScrollReveal
                baseOpacity={0}
                enableBlur={true}
                baseRotation={3}
                blurStrength={8}
                containerClassName="text-center"
                textClassName="text-lg md:text-xl text-gray-300 leading-relaxed"
              >
                A showcase of my latest work, featuring innovative solutions and cutting-edge technologies 
                that push the boundaries of digital experiences.
              </ScrollReveal>
            </div>
          </motion.div>

          {/* Filter Tabs */}
          <motion.div variants={itemVariants} className="flex justify-center mb-8 md:mb-12">
            <div className="glass-morphism rounded-full p-2 max-w-full overflow-x-auto scrollbar-hide">
              <div className="flex space-x-2 px-2 min-w-max">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setFilter(category)}
                    className={`px-4 md:px-6 py-2 md:py-3 rounded-full font-medium transition-all duration-300 text-sm md:text-base whitespace-nowrap ${
                      filter === category
                        ? 'bg-accent-green text-black shadow-lg'
                        : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    {category === 'all' ? 'All Projects' : category}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Projects Grid */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6"
          >
            <AnimatePresence mode="wait">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id || project._id}
                  variants={itemVariants}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="glass-morphism-strong rounded-2xl p-4 md:p-6 group cursor-pointer interactive-card h-full text-left"
                  onClick={() => setSelectedProject(project)}
                >
                  {/* Project Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2 md:space-x-3 flex-1 min-w-0">
                      <div className="text-xl md:text-2xl text-accent-green opacity-80 flex-shrink-0">
                        {getCategoryIcon(project.category)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base md:text-lg font-space-grotesk font-bold text-white group-hover:text-accent-green transition-colors truncate text-left">
                          {project.title}
                        </h3>
                        <span className="text-xs font-jetbrains text-accent-green bg-accent-green/10 px-2 py-1 rounded">
                          {project.category}
                        </span>
                      </div>
                    </div>
                    
                    {/* Status Badge */}
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold bg-${getStatusColor(project.status)}/20 text-${getStatusColor(project.status)} border border-${getStatusColor(project.status)}/30 flex-shrink-0`}>
                      {getStatusText(project.status)}
                    </span>
                  </div>
                  
                  {/* Description */}
                  <p className="text-gray-300 text-sm leading-relaxed mb-4 line-clamp-3 text-left">
                    {project.description}
                  </p>

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.technologies?.slice(0, 3).map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="px-2 py-1 bg-glass-light rounded text-xs font-jetbrains text-accent-green/80"
                      >
                        {tech}
                      </span>
                    ))}
                    {project.technologies?.length > 3 && (
                      <span className="px-2 py-1 bg-glass-light rounded text-xs font-jetbrains text-gray-400">
                        +{project.technologies.length - 3} more
                      </span>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between mt-auto">
                    <div className="flex space-x-2">
                      {project.demo_url && (
                        <div className="p-2 bg-accent-green/20 rounded-lg backdrop-blur-sm">
                          <HiExternalLink className="text-accent-green text-sm" />
                        </div>
                      )}
                      {project.github_url && (
                        <div className="p-2 bg-accent-blue/20 rounded-lg backdrop-blur-sm">
                          <FaGithub className="text-accent-blue text-sm" />
                        </div>
                      )}
                      {project.id === 'aliyah-chatbot' && (
                        <div className="p-2 bg-green-500/20 rounded-lg backdrop-blur-sm">
                          <FaWhatsapp className="text-green-500 text-sm" />
                        </div>
                      )}
                    </div>
                    
                    <span className="text-xs text-gray-400">
                      Click for details
                    </span>
                  </div>
                </motion.div>
              ))}

              {/* CTA Card - Inserted into the grid */}
              <motion.div
                variants={itemVariants}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="glass-morphism-strong rounded-2xl p-4 md:p-6 interactive-card h-full flex flex-col justify-center items-center text-center"
              >
                <div className="mb-4 md:mb-6">
                  <div className="text-3xl md:text-4xl text-accent-green mb-3 md:mb-4">
                    <HiSparkles />
                  </div>
                  <h3 className="text-lg md:text-xl font-space-grotesk font-bold text-white mb-3 md:mb-4">
                    Let's Work Together
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4 md:mb-6">
                    Interested in working together on your next project?
                  </p>
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const element = document.getElementById('contact');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-primary text-sm md:text-base px-4 md:px-6 py-2 md:py-3 w-full"
                >
                  Let's Collaborate →
                </motion.button>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </div>

      {/* Project Modal */}
      {selectedProject && (
        <ProjectModal 
          project={selectedProject} 
          onClose={() => setSelectedProject(null)} 
        />
      )}
    </section>
  );
};

export default Projects;